// Resolves a control's display label according to a user's preferred
// framework, falling back to the TCF id (with a bracketed note of a related
// framework, if any) when the control isn't mapped to that framework.

import { buildControlIndex } from '../constants/controlIndex.js';

export const SELECTABLE_CONTROL_FRAMEWORKS = [
  { id: 'fw-iso27001', label: 'ISO 27001' },
  { id: 'fw-nist-csf-20', label: 'NIST CSF 2.0' },
  { id: 'fw-cis18', label: 'CIS18' },
];

/**
 * @param {string} scfId - canonical TCF control id
 * @param {string|null|undefined} preferredFrameworkId - one of SELECTABLE_CONTROL_FRAMEWORKS ids, or unset
 * @param {string[]|null} [enabledFrameworkIds] - tenant's enabled framework ids, to scope the bracketed fallback
 * @returns {{ label: string, matchedPreferred: boolean, bracketFrameworkId: string|null }}
 */
export function resolveControlLabel(scfId, preferredFrameworkId, enabledFrameworkIds) {
  if (!scfId) return { label: '', matchedPreferred: false, bracketFrameworkId: null };

  const { externalRefs } = buildControlIndex();
  const refs = externalRefs.get(scfId) || [];

  if (preferredFrameworkId) {
    const match = refs.find((r) => r.frameworkId === preferredFrameworkId);
    if (match) return { label: match.externalId, matchedPreferred: true, bracketFrameworkId: null };
  }

  const enabledSet = enabledFrameworkIds ? new Set(enabledFrameworkIds) : null;
  const related = enabledSet ? refs.find((r) => enabledSet.has(r.frameworkId)) : refs[0];

  return {
    label: related ? `${scfId} (also: ${related.externalId})` : scfId,
    matchedPreferred: false,
    bracketFrameworkId: related ? related.frameworkId : null,
  };
}

/**
 * Pure (non-hook) helper for resolving a display label from any control id —
 * a TCF id or a framework-native external id (e.g. "5.1", "GV.OC-01"). Safe
 * to call inside .map() loops, unlike the useResolvedControlLabel hook.
 *
 * @param {string} controlId - TCF id or framework-native external id
 * @param {string|null|undefined} preferredFrameworkId
 * @param {string[]|null} [enabledFrameworkIds]
 */
export function resolveControlDisplayLabel(controlId, preferredFrameworkId, enabledFrameworkIds) {
  if (!controlId) return controlId;

  const { byExternalId, externalRefs } = buildControlIndex();
  const scfId =
    byExternalId.get(controlId) ||
    (typeof controlId === 'string' && controlId.startsWith('SCF-') ? controlId : null);
  if (!scfId) return controlId;

  // Several distinct controls in one framework can cite the same underlying TCF
  // control (e.g. ISO 27001 clauses 4.1-4.4 all cite SCF-MSS-001). If the stored
  // ID is already the preferred framework's own citation, show it unchanged —
  // otherwise resolveControlLabel's "first match" below would collapse every
  // one of those distinct controls onto the same displayed clause.
  if (preferredFrameworkId) {
    const refs = externalRefs.get(scfId) || [];
    const selfMatch = refs.some(
      (r) => r.frameworkId === preferredFrameworkId && r.externalId === controlId
    );
    if (selfMatch) return controlId;
  }

  const { label } = resolveControlLabel(scfId, preferredFrameworkId, enabledFrameworkIds);
  return label || controlId;
}
